import { SKILLS } from './heroes.ts';
import type { EnemyKind } from './nightmares.ts';
import type { UniqueSlot } from './progression.ts';
import { UNIQUES, type UniqueId } from './saga.ts';
import { SIMA_GUARDIANS } from './sima.ts';

/**
 * The feats of the unique pieces (docs/UNICAS.md §2, §3): each awakening stage asks one, done by playing with the
 * piece's power, one at a time (`step` 1 to 4; 5: none left).
 * - I · Costumbre (`use`): the power doing its thing this many times (or this much damage, healing…), in any fight;
 * - II · Prueba (`test`): a hard moment with the power, in one go, this many times;
 * - III · En lo hondo: the test of the II, `min(FEAT.deepTimes, test)` times, in hard places (FEAT_HARD);
 * - IV · Leyenda: FEAT.bosses different big bosses (FEAT_BOSSES) felled with the piece on and its power used
 *   `legendUses` times in each fight.
 * `allies`: a test that counts allies asks at most the campaign's players less one (at least one), §2.2.
 * The numbers are starting ones: measured with `pnpm balance:real` and `pnpm balance:mirror` (§2.2).
 */
export interface FeatDef {
  use: number;
  test: number;
  allies?: number;
}

export const FEATS: Record<UniqueId, FeatDef> = {
  // Weapons (from the Turtle 2, level 5).
  gateBlade: { use: 150, test: 15 },
  garrisonKey: { use: 8000, test: 15 },
  fallenStaff: { use: 1500, test: 15 },
  chapelLamp: { use: 15000, test: 20 },
  watchBow: { use: 1500, test: 20 },
  wallAxe: { use: 1000, test: 15 },
  gasparHammer: { use: 300, test: 25 },
  oldRootStaff: { use: 800, test: 15 },
  // Armours (from the dragon, level 20).
  lastWatch: { use: 20000, test: 10 },
  anvilHarness: { use: 20000, test: 10 },
  frostMantle: { use: 300, test: 10 },
  dwarfMail: { use: 30000, test: 15, allies: 4 },
  wandererCloak: { use: 400, test: 30 },
  boarHide: { use: 200, test: 10 },
  oathPlate: { use: 25000, test: 10 },
  dwarfBark: { use: 20000, test: 10 },
  // Amulets (from the Drowned Dragon, level 40).
  posternMedal: { use: 60, test: 5, allies: 3 },
  maelisKit: { use: 10000, test: 10, allies: 4 },
  stormEye: { use: 20000, test: 15 },
  healerRosary: { use: 20, test: 5, allies: 3 },
  mossWhistle: { use: 25000, test: 15 },
  tailFang: { use: 100, test: 15 },
  vigilCandle: { use: 20000, test: 10 },
  maelisSeed: { use: 20000, test: 15, allies: 4 },
  // The panoply: the warrior's (docs/PANOPLIA.md §10). Before the armour's ¾ and the amulet's ½ (`FEAT.share`).
  butcherCleaver: { use: 300, test: 15 },
  barracksMace: { use: 300, test: 15 },
  wellHook: { use: 150, test: 15 },
  drillPlate: { use: 600, test: 15 },
  frontRankCuirass: { use: 600, test: 15 },
  criersCuirass: { use: 20000, test: 15 },
  menderThimble: { use: 300, test: 10 },
  bellNail: { use: 1000, test: 15 },
  veteranMedallion: { use: 30000, test: 10 },
  // The druid's.
  hiveStaff: { use: 800, test: 15 },
  herbalistStaff: { use: 1000, test: 10 },
  briarStaff: { use: 10000, test: 15 },
  poulticeTunic: { use: 10000, test: 10 },
  goatherdPelt: { use: 20000, test: 10 },
  gardenerPlate: { use: 800, test: 10 },
  undertakerPendant: { use: 2000, test: 10 },
  groundStone: { use: 1000, test: 10 },
  orchardAcorn: { use: 800, test: 10 },
  // The barbarian's and the paladin's.
  shepherdAxe: { use: 1000, test: 15 },
  brawlerAxe: { use: 300, test: 15 },
  carterAxe: { use: 20000, test: 10 },
  bullTusk: { use: 800, test: 15 },
  wolfSkull: { use: 1000, test: 10 },
  tannerFang: { use: 1000, test: 15 },
  foremanPelt: { use: 30000, test: 10, allies: 4 },
  challengerHide: { use: 100, test: 10 },
  ancestorPelts: { use: 3000, test: 10 },
  firstRoundHammer: { use: 1500, test: 15 },
  penitenceHammer: { use: 15000, test: 15 },
  keeperHammer: { use: 15000, test: 15, allies: 3 },
  armsMasterCuirass: { use: 20000, test: 15 },
  armourerCoat: { use: 30000, test: 10 },
  stairPlates: { use: 500, test: 10 },
  blessedStone: { use: 20000, test: 10 },
  elderCharm: { use: 30000, test: 10, allies: 3 },
  prayerBead: { use: 25000, test: 10 },
  // The ranger's.
  hunterBow: { use: 1000, test: 15 },
  trackerBow: { use: 300, test: 15 },
  pantryBow: { use: 1000, test: 15 },
  quarryBow: { use: 1500, test: 15 },
  falconerJerkin: { use: 400, test: 15 },
  signalerCoat: { use: 300, test: 10 },
  trapperJerkin: { use: 40, test: 15 },
  ballistaTip: { use: 25000, test: 10 },
  archerFeather: { use: 1000, test: 10 },
  tunerLens: { use: 3000, test: 15 },
  // The cleric's.
  nurseStaff: { use: 20000, test: 20 },
  watercarrierStaff: { use: 10000, test: 10, allies: 3 },
  lamplighterStaff: { use: 300, test: 15 },
  midwifeStaff: { use: 15000, test: 15 },
  gardenerHabit: { use: 30000, test: 15, allies: 3 },
  cisternStole: { use: 150, test: 10 },
  pilgrimSmock: { use: 20000, test: 15 },
  bronzeSun: { use: 25000, test: 10 },
  infirmaryBell: { use: 30000, test: 10 },
  vigilMedal: { use: 30, test: 5 },
  // The artificer's and the mage's.
  minerPick: { use: 300, test: 15 },
  stokerPoker: { use: 600, test: 15 },
  buildersSquare: { use: 20000, test: 15 },
  clockmakerGear: { use: 10000, test: 15 },
  locksmithRivet: { use: 800, test: 15 },
  rodmanCopper: { use: 300, test: 15 },
  gunnerPlate: { use: 1000, test: 10 },
  masonApron: { use: 20000, test: 10 },
  kennelCoat: { use: 300, test: 15 },
  stormStaff: { use: 1500, test: 15 },
  distillerStaff: { use: 800, test: 15 },
  pantryStaff: { use: 1000, test: 15 },
  whiteWallRobe: { use: 800, test: 15 },
  teacherRobe: { use: 8000, test: 15 },
  jailerSurcoat: { use: 20000, test: 15 },
  messengerCape: { use: 300, test: 15 },
  iceTear: { use: 300, test: 10 },
  dreamStone: { use: 500, test: 10 },
  cometEmber: { use: 25000, test: 10 },
};

/**
 * - `share`: a piece that comes late asks less (bp of the table's I and II, rounded up): the armour three quarters, the amulet half.
 * - `deepTimes`: En lo hondo asks the test this many times at most (never shortened).
 * - `bosses`: the Legend's different big bosses; `uses` / `usesLong`: the power used in each of those fights (`usesLong` for
 *   the ultimates and the powers that wake by themselves).
 * - `catchUp`: a piece given before the feats came, still without its stage I, starts with its feat I this far along per
 *   level gained since it came (bp; capped at the whole), counted from the level its chapter gave it at then (`oldLevel`).
 * - `allyHold`: the sanctuary's and the bloom's tests ask the allies inside together this long (ticks, by piece).
 * - `watch`: how long a test waits to see how it ends (ticks): the ally saved by the lamp, the elite after the smite,
 *   the frozen before they thaw, the fireball's targets.
 */
export const FEAT = {
  share: { weapon: 10000, armor: 7500, amulet: 5000 } as Record<UniqueSlot, number>,
  deepTimes: 10,
  bosses: 3,
  uses: 6,
  usesLong: 2,
  catchUp: 1000,
  oldLevel: { weapon: 5, armor: 10, amulet: 15 } as Record<UniqueSlot, number>,
  allyHold: { dwarfMail: 48, maelisSeed: 72 } as Partial<Record<UniqueId, number>>,
  watch: { lamp: 60, lampBelowBp: 2000, smite: 36, staff: 30, mantle: 24, hook: 48, hawkElite: 600, trap: 600, nurse: 60, midwife: 60, medal: 120, square: 120, chain: 24, binding: 24 },
  /** Gate blade's test: this many stunned with one bash. Wall axe: pulled by one whirl. And so on (§3). */
  need: {
    bash: 4, triangle: 6, staff: 5, pierce: 6, whirl: 8, roots: 6, challenge: 8, burst: 5, mantle: 4, aegis: 5, bear: 6, storm: 8, rain: 10, chain: 3, ink: 6, lowBp: 1000, highBp: 6000,
    // The panoply's (docs/PANOPLIA.md §10): the mace's knocked together, the decoy's left at it, the wall's pushed at once,
    // the shout's challenged, the banner's stunned; the medallion's stand ends below `standLowBp` and mends `standHealBp`.
    clash: 4, decoy: 6, push: 5, shout: 8, banner: 6, standLowBp: 1000, standHealBp: 3000,
    // The druid's: the hive's and the drifting cloud's poisoned, the lash's dragged, the root's attackers, the wolves'
    // kills, the wall's held, the plague's poisoned, the cracks' stunned, the trees' held.
    hive: 6, cloud: 10, lash: 5, rootFoes: 5, rootFoesR: 1200, wolfKills: 6, wallHeld: 10, plague: 20, cracks: 10, trees: 10,
    // The barbarian's: the axe's kills in one throw, the kicks that kill in a row, the brutal blows' climb (from below
    // `brutalLowBp` to above `brutalHighBp` within `brutalTicks`), the charge's stunned, the fear's spread, a stain's bled,
    // the duels won in one Duel, the spirits of one fury.
    axe: 2, kicks: 3, brutalLowBp: 2000, brutalHighBp: 5000, brutalTicks: 120, charge: 8, fear: 10, stain: 5, duels: 3, spirits: 30,
    // The paladin's: the flash's blinded, the atoning blows that kill in a row, the loose hammer's struck, the blessed ones'
    // kills, the intervention's ally below `interveneLowBp` with this many stunned, the rampart's burnt, the avatar's hammers' kills.
    blind: 8, atones: 2, hammer: 6, blessKills: 10, interveneLowBp: 3000, interveneStun: 4, rampart: 8, hammers: 10,
    // The ranger's: a volley's kills, a cloud's poisoned, a leap's kills, a shot's pierced over both lines, an elite's dives,
    // a flare's grounded, a titan arrow's elites, an eye's kills (an elite among them).
    volleyKills: 5, arrowCloud: 6, leapKills: 3, pierceTwo: 8, hawkStuns: 3, flareFly: 4, titanElites: 3, eyeKills: 6,
    // The cleric's: a ray's struck, a light's jumps in a row, a cleanse's returned, a prayer's lights' kills, a column's kills,
    // a raise's stunned; the nurse's and midwife's saves from below `saveBp`.
    rayFoes: 6, lightJumps: 3, returned: 3, prayerKills: 4, columnKills: 8, medalStun: 4, saveBp: 1000,
    // The artificer's: a run of mines and what it felled, the jet's pushed, the mended one below `squareLowBp` (and still
    // standing `watch.square` later), a drone's flight, the barricade's pushed at once, the last spark's reached, the
    // mortar's reached, the allies under Fortify for `apronHold` ticks.
    mineRun: 3, mineKills: 6, bellows: 5, squareLowBp: 2000, droneFlight: 1500, rolled: 8, spark: 8, mortar: 10, apron: 3, apronHold: 48,
    // The mage's: a chain's fallen (within `watch.chain`), a dart's drops eaten, a lance's pierced, a wall's burst, an
    // orb's bitten, a binding all fallen, the flares' reached, the hail's shattered, a rift's dragged, a rain's felled.
    chainKills: 5, drip: 5, lance: 6, wallBurst: 6, orb: 8, binding: 4, flare: 2, flares: 4, hail: 6, rift: 10, rainKills: 15,
  },
  /** The garrison key's triangle counts once this often at most (ticks). */
  triangleEvery: 60,
  /** The postern medal saves the same ally once this often at most (ticks): an oath that keeps it standing counts once. */
  savedEvery: 120,
};

/** What each of a piece's four feats asks (the count), for a hero's piece in its slot. */
export const featGoal = (id: UniqueId, step: number): number => {
  const d = FEATS[id];
  const share = FEAT.share[UNIQUES[id].slot];
  const cut = (n: number): number => Math.trunc((n * share + 9999) / 10000);
  if (step === 1) return cut(d.use);
  if (step === 2) return cut(d.test);
  if (step === 3) return Math.min(FEAT.deepTimes, d.test);
  if (step === 4) return FEAT.bosses;
  return 0;
};

/** The pieces whose power wakes by itself or is an ultimate: the Legend asks it fewer times a fight. */
const LONG: ReadonlySet<UniqueId> = new Set<UniqueId>(['frostMantle', 'boarHide']);
export const legendUses = (id: UniqueId): number => {
  const sk = UNIQUES[id].skill;
  return LONG.has(id) || (sk && SKILLS[sk].slot === 2) ? FEAT.usesLong : FEAT.uses;
};

/** The hard and heroic side sagas' chapters are hard places from this group level on (§2.2). */
export const FEAT_HARD_GROUP = 21;

/**
 * The big bosses the Legend counts (docs/UNICAS.md §2.2) by kind, and what else decides it: the Burn's dragon and the
 * Drowned Dragon, the Obsidian Colossus wherever it is, the Chasm's threshold guardians (from the floor a third of the
 * hero's level), the Unsleeping (any of them, by who he is) and the late Mareas' bosses (from the seventh).
 * Also «what is a boss» for the forms (§3): the same list, with the Warden, the magma lord and the saga's bosses.
 */
export const FEAT_BOSSES: readonly EnemyKind[] = ['dragon', 'sunken', 'colossus', 'witch', 'shell'];
/** The Chasm's threshold guardians (they count only there, from the floor a third of the hero's level). */
export const FEAT_GUARDIANS: readonly EnemyKind[] = ['warden', 'magmaLord', ...SIMA_GUARDIANS];

/**
 * A unique piece's attributes follow its hero (docs/UNICAS.md §2.4): the ones that stretch with the power (STRETCHED_ATTRS)
 * are worth the middle of a rare piece's roll at power hero level + `powerAdd` (a typical place), never less than the
 * piece's own number with its stages; the others keep their own number, `AWAKEN.attrStepBp` more each stage.
 */
export const UNIQUE_STRETCH = { powerAdd: 4 };
